import { useLocation } from 'react-router-dom';
import { useEffect, useMemo, useRef } from 'react';
import ReactGA from 'react-ga4';
import { trackEventPageView } from '@utils/analytics';
import { useTranslation } from 'react-i18next';
import { useInbound } from '@utils/Inbound/useInbound';
import { useSelector } from 'react-redux';
import { getEventDetails } from '@store/slices/eventSlice';
import { safeJSONParse } from '@utils/fn';

export const usePageTracking = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { inboundCookie, actionInbound, updateInboundCookie } = useInbound();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const lastTrackedPath = useRef<string>('');

  const parsedInbound = useMemo(
    () => safeJSONParse(inboundCookie),
    [inboundCookie]
  );
  const isValidInboundForEvent = useMemo(() => {
    if (!parsedInbound || !sluggedEvent?.id) return false;
    const cookieEvent = String(parsedInbound?.event ?? '');
    return cookieEvent.length > 0 && cookieEvent === String(sluggedEvent.id);
  }, [parsedInbound, sluggedEvent?.id]);

  useEffect(() => {
    const currentPath = location.pathname;

    // Skip if we've already tracked this exact path recently
    if (lastTrackedPath.current === currentPath) {
      return;
    }

    // Track page view
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname,
    });

    // Track specific page types based on URL patterns
    const pathname = location.pathname;

    // Get the translated URL patterns
    const eventsUrl = t('events-url');

    // Check if it's an event page (contains /events/ in the path)
    const eventMatch = pathname.match(
      new RegExp(`${eventsUrl.replace('/', '\\/')}\\/([^\\/]+)`)
    );

    if (eventMatch?.[1] && isValidInboundForEvent) {
      trackEventPageView(eventMatch[1]);

      // Send inbound page_view only when cookie is valid for current event
      const currentUrl = window.location.href;
      const truncatedUrl =
        currentUrl.length > 255 ? currentUrl.substring(0, 255) : currentUrl;

      void actionInbound({
        event: parsedInbound?.event,
        path: truncatedUrl,
        action: 'page_view',
        first_name: parsedInbound?.first_name,
        last_name: parsedInbound?.last_name,
        salutation: parsedInbound?.salutation,
        company_industry: parsedInbound?.company_industry,
        email: parsedInbound?.email,
        company_name: parsedInbound?.company_name,
        job_title: parsedInbound?.job_title,
        job_responsibility: parsedInbound?.job_responsibility,
        job_level: parsedInbound?.job_level,
        company_size: parsedInbound?.company_size,
        company_type_of_business: parsedInbound?.company_type_of_business,
        address_country: parsedInbound?.address_country,
      });

      // Update the last tracked path
      lastTrackedPath.current = currentPath;
    }
  }, [
    location?.pathname,
    t,
    isValidInboundForEvent,
    actionInbound,
    parsedInbound?.event,
    parsedInbound?.email,
    parsedInbound?.company_name,
    parsedInbound?.job_title,
    parsedInbound?.job_responsibility,
    parsedInbound?.job_level,
    parsedInbound?.company_size,
    parsedInbound?.company_type_of_business,
    parsedInbound?.address_country,
    parsedInbound?.first_name,
    parsedInbound?.last_name,
    parsedInbound?.salutation,
    parsedInbound?.company_industry,
  ]);

  // Listen for inbound registration completion event
  useEffect(() => {
    const handleInboundRegistrationComplete = (event: CustomEvent) => {
      const newInboundData = event.detail;
      console.log(
        'Inbound registration completed, updating cookie:',
        newInboundData
      );
      updateInboundCookie(newInboundData);
    };

    window.addEventListener(
      'inboundRegistrationComplete',
      handleInboundRegistrationComplete as EventListener
    );

    return () => {
      window.removeEventListener(
        'inboundRegistrationComplete',
        handleInboundRegistrationComplete as EventListener
      );
    };
  }, [updateInboundCookie]);
};
